import i18next from 'i18next';
import {List, Map} from 'immutable';
import moment, {type Moment} from 'moment-timezone';

import generateUUID from 'web-app/util/uuid';
import {type PlanState} from 'web-app/react/entities/common/plan-state';
import * as StringUtils from 'web-app/util/string';
import PlanBehaviorIds from 'web-app/behaviors/plan-behavior-ids';
import {hasValue} from 'web-app/util/typescript';

import ProductBookingEntity from '../common/product-booking';
import {SessionBooking} from '../common/session-booking';
import Session from '../sessions/model';
import * as CostHelpers from './cost-helpers';
import {type Plan, type Discount, PlanPart, Billing} from './model';
import * as Constants from './constants';

export const getWeekdayTitle = (weekday: string) => {
    switch (weekday) {
        case 'MONDAY':
            return i18next.t('weekDays.full.1');
        case 'TUESDAY':
            return i18next.t('weekDays.full.2');
        case 'WEDNESDAY':
            return i18next.t('weekDays.full.3');
        case 'THURSDAY':
            return i18next.t('weekDays.full.4');
        case 'FRIDAY':
            return i18next.t('weekDays.full.5');
        case 'SATURDAY':
            return i18next.t('weekDays.full.6');
        case 'SUNDAY':
            return i18next.t('weekDays.full.7');
        default:
            return StringUtils.capitalizeFirstLetter(weekday, true);
    }
};

export const planWarnings = (plan: Plan): List<any> => {
    const warningObject = plan.behaviors.behaviorForId(PlanBehaviorIds.ShowPlanWarnings);

    return warningObject
        ? List(warningObject.payload.warnings).filter(
              (warning: any) => warning.severity === 'ERROR' || Constants.whitelistedWarnings.includes(warning.error),
          )
        : List();
};

export const planHasWarnings = (plan: Plan) => {
    return planWarnings(plan).size > 0;
};

/**
 * Check if a plan has a specific warning. Does not take the warning's severity into account
 *
 * @param errorCode
 * @param plan
 */
export const planHasWarning = (errorCode: number, plan: Plan): boolean => {
    const warningObject = plan.behaviors.behaviorForId(PlanBehaviorIds.ShowPlanWarnings);

    return warningObject
        ? List(warningObject.payload.warnings).some((planWarning: any) => planWarning.error === errorCode)
        : false;
};

export const getActivePlanState = (planStates: List<PlanState>) => {
    if (planStates.size === 0) {
        return null;
    }

    const today = moment().startOf('day');

    const stateActiveToday = planStates.find(state => Boolean(state.to?.isSameOrAfter(today, 'd')));

    return stateActiveToday ?? planStates.last() ?? null;
};

export const removeSessionBookingFromPlanPart = (planPart: PlanPart, bookingId: string, session: any, day: string) => {
    const isNonFlexiblePackage =
        session && !session.flexibleHours && session.sessionType === Session.SessionType.PACKAGE_SESSION;
    const removeAllBookingsOnDay = session && !bookingId && day;
    if (isNonFlexiblePackage) {
        return planPart.merge({
            sessionBookings: planPart.sessionBookings.filter(
                sessionBooking => sessionBooking.sessionId !== session.sessionId,
            ),
        });
    } else if (removeAllBookingsOnDay) {
        return planPart.merge({
            sessionBookings: planPart.sessionBookings.filter(
                booking => booking.day !== day || booking.sessionId !== session.sessionId,
            ),
        });
    }

    return planPart.merge({
        sessionBookings: planPart.sessionBookings.filter(booking => booking.bookingId !== bookingId),
    });
};

export const removeAllBookingsFromPlanPart = (planPart: PlanPart) => {
    return planPart.merge({
        sessionBookings: List(),
        productBookings: List(),
    });
};

export const removeAllDiscountsFromPlanPart = (planPart: PlanPart) => {
    return planPart.merge({
        discounts: List(),
    });
};

const addPackageToPlanPart = (planPart: PlanPart, session: any) => {
    const newPackageBookings = session
        ? session.availability.map(
              (av: any) =>
                  new SessionBooking({
                      day: av.day,
                      bookingId: generateUUID(),
                      sessionId: session.sessionId,
                      sessionVersion: session.version,
                      start: session.flexibleHours ? av.start : null,
                      end: session.flexibleHours ? av.end : null,
                  }),
          )
        : List();

    return planPart.merge({
        sessionBookings: planPart.sessionBookings.concat(newPackageBookings),
    });
};

export const addSessionToPlanPart = (planPart: PlanPart, session: any, day: string) => {
    if (session.sessionType === Session.SessionType.PACKAGE_SESSION && !session.flexibleHours) {
        return addPackageToPlanPart(planPart, session);
    }

    const dayAvailability = session.availability.find((av: any) => av.day === day);

    if (!dayAvailability) {
        return planPart;
    }

    const newBooking = new SessionBooking({
        day,
        sessionId: session.sessionId,
        start: session.flexibleHours ? dayAvailability.start : null,
        end: session.flexibleHours ? dayAvailability.end : null,
        bookingId: generateUUID(),
    });

    return planPart.merge({
        sessionBookings: planPart.sessionBookings.push(newBooking),
    });
};

export const updatePlanPartPackage = (planPart: PlanPart, newPackage: any, session: any, oldPackage: any) => {
    const filteredBookings = oldPackage
        ? planPart.sessionBookings.filter(booking => booking.sessionId !== oldPackage.sessionId)
        : planPart.sessionBookings;

    const filteredPlanPart = planPart.merge({
        sessionBookings: filteredBookings,
    });

    return newPackage && session ? addPackageToPlanPart(filteredPlanPart, session) : filteredPlanPart;
};

export const setPlanPartPackages = (planPart: PlanPart, newPackages: Array<any>) =>
    newPackages.reduce(
        (acc, session) => addPackageToPlanPart(acc, session),
        planPart.merge({
            sessionBookings: List(),
        }),
    );

export const updateSessionBookingOnPlanPart = (
    planPart: PlanPart,
    bookingId: string,
    updater: (booking: SessionBooking) => SessionBooking,
) => {
    const bookingIndex = planPart.sessionBookings.findIndex(booking => booking.bookingId === bookingId);

    if (bookingIndex < 0) {
        return planPart;
    }

    return planPart.merge({
        sessionBookings: planPart.sessionBookings.update(bookingIndex, updater),
    });
};

export const addProductToPlanPart = (planPart: PlanPart, product: any, amount: number, day: string) => {
    const newBooking = new ProductBookingEntity.record({
        day,
        productId: product.productId,
        amount,
    });

    const bookingIndex = planPart.productBookings.findIndex(
        booking => booking.day === day && booking.productId === product.productId,
    );

    if (bookingIndex > -1) {
        return planPart.merge({
            productBookings: planPart.productBookings.set(bookingIndex, newBooking),
        });
    } else {
        return planPart.merge({
            productBookings: planPart.productBookings.push(newBooking),
        });
    }
};

export const removeProductBookingFromPlanPart = (planPart: PlanPart, productId: string, day: string) => {
    return planPart.merge({
        productBookings: planPart.productBookings.filter(
            booking => booking.day !== day || booking.productId !== productId,
        ),
    });
};

export const isValidPlanPartSettings = (planPart: PlanPart): boolean =>
    planPart.termTimeOnly !== null &&
    planPart.termTimeOnly !== undefined &&
    Boolean(planPart.billingProfileId) &&
    Boolean(planPart.billing) &&
    Boolean(planPart.billing.id) &&
    Boolean(planPart.attendanceScheduleId);

export const isValidPlanSettings = (plan: Plan): boolean => {
    return plan.planParts.every(part => isValidPlanPartSettings(part));
};

/*
 * Updates session bookings on a plan part to match the flexibility on the new sessions in the following ways:
 * 1. If the new session version is flexible but the booking is not, update to match the default booking times on the session
 * 2. If the new session version is non-flexible, & the booking is flexible, & the old booking times matches the new session times
 * */
export const alignPlanPartWithSessionFlexibility = (planPart: PlanPart, newSessions: any[]) => {
    if (newSessions.length === 0) {
        return planPart;
    }

    const sessionsMap = Map(newSessions.map((session: any) => [session.sessionId, session]));

    return planPart.merge({
        sessionBookings: planPart.sessionBookings.map(booking => {
            const session = sessionsMap.get(booking.sessionId) as any;
            if (!session || (session.flexibleHours && booking.start) || (!session.flexibleHours && !booking.start)) {
                return booking;
            }
            const availability = session.availability.find((av: any) => av.day === booking.day);
            if (!availability) {
                return booking;
            }

            if (
                !session.flexibleHours &&
                booking.start &&
                booking.end &&
                booking.start.isSame(availability.start, 'minute') &&
                booking.end.isSame(availability.end, 'minute')
            ) {
                return booking.merge({
                    start: null,
                    end: null,
                });
            }

            return booking.merge({
                start: availability.start,
                end: availability.end,
            });
        }),
    });
};

const compareSurcharge = (a: Discount, b: Discount) => {
    if (a.isSurcharge === b.isSurcharge) {
        return 0;
    }
    if (b.isSurcharge) {
        return -1;
    }
    return 1;
};

export const sortDiscounts = (discounts: Discount[]) =>
    discounts.sort((a, b) => {
        if (a.origin === CostHelpers.DISCOUNT_ORIGIN_CUSTOM && b.origin === CostHelpers.DISCOUNT_ORIGIN_CUSTOM) {
            return compareSurcharge(a, b);
        }
        if (b.origin === CostHelpers.DISCOUNT_ORIGIN_CUSTOM) {
            return -1;
        }
        if (a.origin === CostHelpers.DISCOUNT_ORIGIN_CUSTOM) {
            return 1;
        }
        const aOrdering = a.discountPresetId ? a.ordering : null;
        const bOrdering = b.discountPresetId ? b.ordering : null;

        if (!aOrdering || !bOrdering) {
            return 0;
        }

        return aOrdering - bOrdering;
    });

export const addPlanPartToPlan = (plan: Plan) =>
    plan.merge({
        planParts: plan.planParts.push(
            new PlanPart({
                planPartId: generateUUID(),
            }),
        ),
    });

const updateBillingOnPlanPart = (planPart: PlanPart, newBilling: Billing) => {
    const oldBilling = planPart.billing;
    const isActuals = CostHelpers.billingIsActuals(newBilling);
    const isMonthly = CostHelpers.billingIsMonthly(newBilling);
    if ((!oldBilling || oldBilling.id !== newBilling.id) && (isActuals || isMonthly)) {
        const period = isMonthly ? Constants.FREQUENCY.MONTHLY : Constants.FREQUENCY.WEEKLY;
        const discounts = planPart.discounts;
        return planPart.merge({
            discounts: discounts
                .filter(discount => !isActuals || discount.isPercent) // If it is an actuals discount it must be a percentage
                .map(discount => {
                    if (discount.isPercent || (isActuals && discount.isSurcharge)) {
                        // Discounts by percentage should never be changed
                        // and surcharges aren't valid in actuals and is handled elsewhere
                        return discount;
                    } else if (isActuals && !discount.isPercent && discount.origin === 'custom') {
                        return discount.merge({
                            amount: 0,
                            isPercent: true,
                            period,
                        });
                    }

                    return discount.merge({
                        period,
                    });
                }),
            billing: newBilling,
        });
    }

    return planPart.merge({
        billing: newBilling,
    });
};

export const getBillingFromBillingProfile = (billingProfile: any) => {
    const {annualization, billingScheme} = billingProfile;

    return new Billing({
        id: billingScheme.id,
        invoices: annualization ? annualization.invoices : 12,
        weeksOfCare: annualization ? annualization.weeksOfCare : 52,
        title: billingScheme.title,
    });
};

export const setBillingProfileOnPlanPart = (planPart: PlanPart, billingProfile?: any, override?: boolean) => {
    if (!billingProfile) {
        return planPart.merge({
            billingProfileId: null,
            termTimeOnly: undefined,
            attendanceScheduleId: null,
            billing: undefined,
        });
    }

    const {attendanceSchedule} = billingProfile;

    const termTimeOnly =
        attendanceSchedule && attendanceSchedule.fullYear !== null ? !attendanceSchedule.fullYear : undefined;

    const newBilling = getBillingFromBillingProfile(billingProfile);

    return updateBillingOnPlanPart(planPart, newBilling).merge({
        billingProfileId: override ? planPart.billingProfileId : billingProfile.billingProfileId,
        termTimeOnly,
        attendanceScheduleId: attendanceSchedule ? attendanceSchedule.id : null,
        billing: newBilling,
    });
};

export const getActivePlan = (plans: List<Plan>) => {
    const startOfToday = moment().startOf('day');

    const currentPlans = plans.filter(plan => isPlanActiveOnDate(startOfToday, plan));

    // There can be only one
    return currentPlans.first();
};

export enum BookingType {
    Session = 'Session',
    Package = 'Package',
    Product = 'Product',
}

export const hasSessionBooking = (sessionId: string, plan: Plan, isFinanceV1: boolean): boolean => {
    return plan.planParts.some(planPart =>
        planPart.sessionBookings.some(sessionBooking => {
            // Customers on FinanceV2 should only be able to filter on the overall sessions and not on specific versions
            return !isFinanceV1 ? sessionBooking.sessionId === sessionId : sessionBooking.sessionVersion === sessionId;
        }),
    );
};

export const hasProductBooking = (productId: string, plan: Plan, isFinanceV1: boolean): boolean => {
    return plan.planParts.some(planPart => {
        return planPart.productBookings.some(productBooking => {
            return !isFinanceV1 ? productBooking.productId === productId : productBooking.productVersion === productId;
        });
    });
};

export const isPlanActiveOnDate = (date: Moment, plan: Plan) => {
    // This shouldn't be possible. A plan always has a start date
    if (!hasValue(plan.from)) {
        return false;
    }

    const isGivenDateAfterPlanFrom = plan.from.isSameOrBefore(date, 'day');

    if (!hasValue(plan.to)) {
        return isGivenDateAfterPlanFrom;
    }

    // Plan to date is same as from data of the next plan in the list
    // that is why we are checking range with isAfter
    return isGivenDateAfterPlanFrom && plan.to.isAfter(date, 'day');
};

export const isPlanExpiredByDate = (date: Moment, plan: Plan) => {
    if (!hasValue(plan.from) || !hasValue(plan.to)) {
        return false;
    }

    return plan.from.isSameOrBefore(date, 'day') && plan.to.isSameOrBefore(date, 'day');
};

export const hasDiscountWithPresetId = (discountPresetId: string, plan: Plan): boolean => {
    return plan.planParts.some(planPart => {
        return planPart.discounts.some(discount => discount.discountPresetId === discountPresetId);
    });
};
